import React, { useState } from 'react';
import CognitoAuthService from '../services/CognitoAuthService';

function ForgotPassword({ isOpen, onClose, onSwitchToLogin }) {
  const [step, setStep] = useState('email'); // 'email' or 'reset'
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const cognitoService = new CognitoAuthService();

  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await cognitoService.forgotPassword(email);
      setMessage('Password reset email sent successfully! Please check your email.');
      setStep('reset');
    } catch (err) {
      console.error('Forgot password error:', err);
      setMessage('Error: ' + (err.message || 'Failed to send reset email'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage('Error: Passwords do not match!');
      return;
    }
    
    setLoading(true);
    setMessage('');

    try {
      await cognitoService.resetPassword(email, verificationCode, newPassword);
      setMessage('Password reset successful! You can now login with your new password.');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      setMessage('Error: ' + (err.message || 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setMessage('');
    setStep('email');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header auth-header">
          <button className="close-button-top" onClick={handleClose}>×</button>
          <h2 className="modal-title auth-title">
            {step === 'email' ? 'Forgot Password' : 'Reset Password'}
          </h2>
        </div>
        
        {step === 'email' ? (
          <form onSubmit={handleSendResetEmail} className="auth-form">
            <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            
            {message && (
              <div className="message" style={{ 
                color: message.includes('Error') ? 'red' : 'green', 
                marginBottom: '10px', 
                textAlign: 'center' 
              }}>
                {message}
              </div>
            )}
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="auth-submit-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </div>
            
            <div className="auth-footer">
              <p>Remember your password? <span className="link-text" onClick={onSwitchToLogin}>Login</span></p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="auth-form">
            <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
              Enter the verification code from your email and create a new password.
            </p>
            
            {message && (
              <div className="message" style={{ 
                color: message.includes('Error') ? 'red' : 'green', 
                marginBottom: '10px', 
                textAlign: 'center' 
              }}>
                {message}
              </div>
            )}
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter verification code"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-with-label">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="auth-submit-button" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
            
            <div className="auth-footer">
              <p>Remember your password? <span className="link-text" onClick={onSwitchToLogin}>Login</span></p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword; 